import React, { useState } from 'react';
import { Modal, ModalHeader, ModalContent } from '@/shared/components/ui';
import { useCustomActionLogs, useCustomActionLogStats } from '../hooks';
import type { CustomActionResponse, CustomActionLogResponse, CustomActionLogStats } from '@troia-v3/types';
import { Icon } from '@/shared/icons'
interface CustomActionLogsModalProps {
  isOpen: boolean;
  onClose: () => void;
  action: CustomActionResponse;
}

export const CustomActionLogsModal: React.FC<CustomActionLogsModalProps> = ({
  isOpen,
  onClose,
  action,
}) => {
  const [selectedLog, setSelectedLog] = useState<CustomActionLogResponse | null>(null);
  const [page, setPage] = useState(1);

  const { data: logsData, isLoading: logsLoading } = useCustomActionLogs(
    action.id,
    { page, limit: 15 }
  );
  const { data: statsData } = useCustomActionLogStats(action.id);

  const logs: CustomActionLogResponse[] = logsData?.data || [];
  const pagination = logsData?.pagination;
  const stats: CustomActionLogStats | undefined = statsData?.data;

  const handleClose = () => {
    setSelectedLog(null);
    setPage(1);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="4xl">
      <ModalHeader onClose={handleClose}>
        {selectedLog ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedLog(null)}
              className="p-1 rounded transition-colors"
              style={{ color: '#64748b' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Icon name="arrow-left" className="w-4 h-4" />
            </button>
            <h2 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
              Detalhes da Execução
            </h2>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg" style={{ backgroundColor: '#ede9fe' }}>
              <Icon name="activity" className="w-4 h-4" style={{ color: '#7c3aed' }} />
            </div>
            <div>
              <h2 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
                Logs de Requisições
              </h2>
              <p className="text-xs" style={{ color: '#64748b' }}>
                {action.displayName}
              </p>
            </div>
          </div>
        )}
      </ModalHeader>

      <ModalContent padding={false}>
        {selectedLog ? (
          <LogDetailView log={selectedLog} />
        ) : (
          <LogListView
            logs={logs}
            stats={stats}
            isLoading={logsLoading}
            pagination={pagination}
            page={page}
            onPageChange={setPage}
            onSelectLog={setSelectedLog}
          />
        )}
      </ModalContent>
    </Modal>
  );
};

CustomActionLogsModal.displayName = 'CustomActionLogsModal';

// ============================================================================
// Stats Bar
// ============================================================================

interface StatsBarProps {
  stats: CustomActionLogStats;
}

const StatsBar: React.FC<StatsBarProps> = ({ stats }) => {
  return (
    <div
      className="grid grid-cols-3 gap-3 p-4 border-b"
      style={{ borderColor: '#e5e7eb' }}
    >
      <div className="text-center">
        <p className="text-xs font-medium" style={{ color: '#64748b' }}>
          Total
        </p>
        <p className="text-lg font-bold" style={{ color: '#1e293b' }}>
          {stats.totalExecutions}
        </p>
      </div>
      <div className="text-center">
        <p className="text-xs font-medium" style={{ color: '#64748b' }}>
          Taxa de Sucesso
        </p>
        <p
          className="text-lg font-bold"
          style={{ color: stats.successRate >= 80 ? '#10b981' : stats.successRate >= 50 ? '#f59e0b' : '#ef4444' }}
        >
          {stats.successRate.toFixed(1)}%
        </p>
      </div>
      <div className="text-center">
        <p className="text-xs font-medium" style={{ color: '#64748b' }}>
          Tempo Médio
        </p>
        <p className="text-lg font-bold" style={{ color: '#1e293b' }}>
          {stats.avgExecutionTime.toFixed(0)}ms
        </p>
      </div>
    </div>
  );
};

// ============================================================================
// Log List View
// ============================================================================

interface LogListViewProps {
  logs: CustomActionLogResponse[];
  stats?: CustomActionLogStats;
  isLoading: boolean;
  pagination?: { page: number; totalPages: number; total: number };
  page: number;
  onPageChange: (page: number) => void;
  onSelectLog: (log: CustomActionLogResponse) => void;
}

const LogListView: React.FC<LogListViewProps> = ({
  logs,
  stats,
  isLoading,
  pagination,
  page,
  onPageChange,
  onSelectLog,
}) => {
  return (
    <div className="flex flex-col" style={{ maxHeight: '60vh' }}>
      {stats && <StatsBar stats={stats} />}

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div
              className="animate-spin rounded-full h-6 w-6 border-b-2"
              style={{ borderColor: '#7c3aed' }}
            />
          </div>
        )}

        {!isLoading && logs.length === 0 && (
          <div className="text-center py-12">
            <Icon name="activity" className="w-10 h-10 mx-auto mb-3" style={{ color: '#d1d5db' }} />
            <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
              Nenhum log encontrado
            </p>
            <p className="text-xs mt-1" style={{ color: '#64748b' }}>
              As execuções desta ferramenta aparecerão aqui
            </p>
          </div>
        )}

        {!isLoading && logs.length > 0 && (
          <div>
            {/* Table header */}
            <div
              className="grid grid-cols-12 gap-2 px-4 py-2 text-xs font-medium"
              style={{ backgroundColor: '#f8fafc', color: '#64748b', borderBottom: '1px solid #e5e7eb' }}
            >
              <div className="col-span-3">Data/Hora</div>
              <div className="col-span-3">Requisição</div>
              <div className="col-span-1 text-center">Status</div>
              <div className="col-span-2 text-center">Resultado</div>
              <div className="col-span-1 text-center">Tempo</div>
              <div className="col-span-2 text-center">Origem</div>
            </div>

            {/* Rows */}
            {logs.map((log) => (
              <button
                key={log.id}
                onClick={() => onSelectLog(log)}
                className="w-full grid grid-cols-12 gap-2 px-4 py-2.5 text-left text-sm transition-colors"
                style={{ borderBottom: '1px solid #f1f5f9', color: '#1e293b' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div className="col-span-3 text-xs" style={{ color: '#64748b' }}>
                  {formatDate(log.createdAt)}
                </div>
                <div className="col-span-3 flex items-center gap-1 truncate">
                  <span
                    className="text-xs font-mono font-semibold px-1.5 py-0.5 rounded"
                    style={{
                      backgroundColor: getMethodColor(log.request.method).bg,
                      color: getMethodColor(log.request.method).text,
                    }}
                  >
                    {log.request.method}
                  </span>
                  <span className="text-xs truncate" style={{ color: '#64748b' }}>
                    {truncateUrl(log.request.url)}
                  </span>
                </div>
                <div className="col-span-1 text-center">
                  {log.response?.statusCode && (
                    <span
                      className="text-xs font-mono font-medium"
                      style={{
                        color: log.response.statusCode < 400 ? '#10b981' : '#ef4444',
                      }}
                    >
                      {log.response.statusCode}
                    </span>
                  )}
                </div>
                <div className="col-span-2 flex items-center justify-center gap-1">
                  {log.success ? (
                    <>
                      <Icon name="check-circle" className="w-3.5 h-3.5" style={{ color: '#10b981' }} />
                      <span className="text-xs" style={{ color: '#10b981' }}>
                        Sucesso
                      </span>
                    </>
                  ) : (
                    <>
                      <Icon name="x-circle" className="w-3.5 h-3.5" style={{ color: '#ef4444' }} />
                      <span className="text-xs" style={{ color: '#ef4444' }}>
                        Falha
                      </span>
                    </>
                  )}
                </div>
                <div className="col-span-1 text-center">
                  <span className="text-xs flex items-center justify-center gap-0.5" style={{ color: '#64748b' }}>
                    <Icon name="clock" className="w-3 h-3" />
                    {log.executionTime}ms
                  </span>
                </div>
                <div className="col-span-2 text-center">
                  <span
                    className="text-xs px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: getSourceColor(log.source).bg,
                      color: getSourceColor(log.source).text,
                    }}
                  >
                    {getSourceLabel(log.source)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div
          className="flex items-center justify-between px-4 py-3 border-t"
          style={{ borderColor: '#e5e7eb' }}
        >
          <p className="text-xs" style={{ color: '#64748b' }}>
            Página {pagination.page} de {pagination.totalPages} ({pagination.total} registros)
          </p>
          <div className="flex gap-2">
            {/* F54: mini-padrão de paginação de modal de logs — IGUAL ao WebhookLogsModal */}
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page === 1}
              className="px-3 py-1 text-xs rounded border transition-colors disabled:opacity-50"
              style={{ backgroundColor: '#ffffff', borderColor: '#e5e7eb', color: '#1e293b' }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#f1f5f9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }}
              data-testid="tool-logs-prev-page"
            >
              Anterior
            </button>
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page === pagination.totalPages}
              className="px-3 py-1 text-xs rounded border transition-colors disabled:opacity-50"
              style={{ backgroundColor: '#ffffff', borderColor: '#e5e7eb', color: '#1e293b' }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#f1f5f9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }}
              data-testid="tool-logs-next-page"
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Log Detail View
// ============================================================================

interface LogDetailViewProps {
  log: CustomActionLogResponse;
}

const LogDetailView: React.FC<LogDetailViewProps> = ({ log }) => {
  return (
    <div className="overflow-y-auto" style={{ maxHeight: '65vh' }}>
      {/* Summary */}
      <div className="grid grid-cols-4 gap-3 p-4 border-b" style={{ borderColor: '#e5e7eb' }}>
        <div>
          <p className="text-xs font-medium" style={{ color: '#64748b' }}>Status</p>
          <div className="flex items-center gap-1 mt-1">
            {log.success ? (
              <>
                <Icon name="check-circle" className="w-4 h-4" style={{ color: '#10b981' }} />
                <span className="text-sm font-medium" style={{ color: '#10b981' }}>Sucesso</span>
              </>
            ) : (
              <>
                <Icon name="x-circle" className="w-4 h-4" style={{ color: '#ef4444' }} />
                <span className="text-sm font-medium" style={{ color: '#ef4444' }}>Falha</span>
              </>
            )}
          </div>
        </div>
        <div>
          <p className="text-xs font-medium" style={{ color: '#64748b' }}>Tempo</p>
          <p className="text-sm font-medium mt-1" style={{ color: '#1e293b' }}>
            {log.executionTime}ms
          </p>
        </div>
        <div>
          <p className="text-xs font-medium" style={{ color: '#64748b' }}>Origem</p>
          <span
            className="text-xs px-2 py-0.5 rounded-full inline-block mt-1"
            style={{
              backgroundColor: getSourceColor(log.source).bg,
              color: getSourceColor(log.source).text,
            }}
          >
            {getSourceLabel(log.source)}
          </span>
        </div>
        <div>
          <p className="text-xs font-medium" style={{ color: '#64748b' }}>Data</p>
          <p className="text-sm mt-1" style={{ color: '#1e293b' }}>
            {formatDate(log.createdAt)}
          </p>
        </div>
      </div>

      {/* Error message if failed */}
      {log.error && (
        <div className="mx-4 mt-4 p-3 rounded-lg" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
          <p className="text-xs font-medium" style={{ color: '#991b1b' }}>Erro</p>
          <p className="text-sm mt-1" style={{ color: '#b91c1c' }}>{log.error}</p>
        </div>
      )}

      {/* Collapsible Sections */}
      <div className="p-4 space-y-3">
        <CollapsibleSection title="Request" defaultOpen>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span
                className="text-xs font-mono font-semibold px-2 py-1 rounded"
                style={{
                  backgroundColor: getMethodColor(log.request.method).bg,
                  color: getMethodColor(log.request.method).text,
                }}
              >
                {log.request.method}
              </span>
              <span className="text-sm font-mono break-all" style={{ color: '#1e293b' }}>
                {log.request.url}
              </span>
            </div>

            {log.request.parameters && Object.keys(log.request.parameters).length > 0 && (
              <div>
                <p className="text-xs font-medium mb-1" style={{ color: '#64748b' }}>Parâmetros</p>
                <JsonBlock data={log.request.parameters} />
              </div>
            )}

            {log.request.headers && Object.keys(log.request.headers).length > 0 && (
              <div>
                <p className="text-xs font-medium mb-1" style={{ color: '#64748b' }}>Headers</p>
                <JsonBlock data={log.request.headers} />
              </div>
            )}

            {log.request.body !== undefined && log.request.body !== null && (
              <div>
                <p className="text-xs font-medium mb-1" style={{ color: '#64748b' }}>Body</p>
                <JsonBlock data={log.request.body} />
              </div>
            )}
          </div>
        </CollapsibleSection>

        {log.response && (
          <CollapsibleSection title="Response" defaultOpen>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium" style={{ color: '#64748b' }}>Status Code:</span>
                <span
                  className="text-sm font-mono font-bold"
                  style={{ color: log.response.statusCode < 400 ? '#10b981' : '#ef4444' }}
                >
                  {log.response.statusCode}
                </span>
              </div>

              {log.response.headers && Object.keys(log.response.headers).length > 0 && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: '#64748b' }}>Headers</p>
                  <JsonBlock data={log.response.headers} />
                </div>
              )}

              {log.response.body !== undefined && log.response.body !== null && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: '#64748b' }}>Body</p>
                  <JsonBlock data={log.response.body} />
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

        {log.aiProcessing?.enabled && (
          <CollapsibleSection title="AI Processing">
            <div className="space-y-3">
              {log.aiProcessing.prompt && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: '#64748b' }}>Prompt</p>
                  <pre
                    className="text-xs p-3 rounded overflow-x-auto whitespace-pre-wrap"
                    style={{ backgroundColor: '#f8fafc', color: '#1e293b', border: '1px solid #e5e7eb' }}
                  >
                    {log.aiProcessing.prompt}
                  </pre>
                </div>
              )}

              {log.aiProcessing.result !== undefined && log.aiProcessing.result !== null && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: '#64748b' }}>Resultado</p>
                  <JsonBlock data={log.aiProcessing.result} />
                </div>
              )}

              {log.aiProcessing.error && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: '#991b1b' }}>Erro AI</p>
                  <pre
                    className="text-xs p-3 rounded overflow-x-auto whitespace-pre-wrap"
                    style={{ backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}
                  >
                    {log.aiProcessing.error}
                  </pre>
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Collapsible Section
// ============================================================================

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  children,
  defaultOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="rounded-lg" style={{ border: '1px solid #e5e7eb' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium transition-colors"
        style={{ color: '#1e293b', backgroundColor: '#f8fafc', borderRadius: isOpen ? '0.5rem 0.5rem 0 0' : '0.5rem' }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f1f5f9';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#f8fafc';
        }}
      >
        {title}
        {isOpen ? (
          <Icon name="chevron-down" className="w-4 h-4" style={{ color: '#64748b' }} />
        ) : (
          <Icon name="chevron-right" className="w-4 h-4" style={{ color: '#64748b' }} />
        )}
      </button>
      {isOpen && (
        <div className="px-3 py-3" style={{ borderTop: '1px solid #e5e7eb' }}>
          {children}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// JSON Block
// ============================================================================

interface JsonBlockProps {
  data: unknown;
}

const JsonBlock: React.FC<JsonBlockProps> = ({ data }) => {
  const formatted = typeof data === 'string' ? data : JSON.stringify(data, null, 2);

  return (
    <pre
      className="text-xs p-3 rounded overflow-x-auto font-mono"
      style={{
        backgroundColor: '#f8fafc',
        color: '#1e293b',
        border: '1px solid #e5e7eb',
        maxHeight: '300px',
        overflowY: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {formatted}
    </pre>
  );
};

// ============================================================================
// Helpers
// ============================================================================

function formatDate(dateStr: Date | string): string {
  const date = new Date(dateStr);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function truncateUrl(url: string): string {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname;
    if (path.length > 30) {
      return '...' + path.slice(-27);
    }
    return path;
  } catch {
    return url.length > 30 ? '...' + url.slice(-27) : url;
  }
}

function getMethodColor(method: string): { bg: string; text: string } {
  const colors: Record<string, { bg: string; text: string }> = {
    GET: { bg: '#dbeafe', text: '#1d4ed8' },
    POST: { bg: '#dcfce7', text: '#15803d' },
    PUT: { bg: '#fef3c7', text: '#b45309' },
    PATCH: { bg: '#fef3c7', text: '#b45309' },
    DELETE: { bg: '#fee2e2', text: '#b91c1c' },
  };
  return colors[method.toUpperCase()] || { bg: '#f1f5f9', text: '#475569' };
}

function getSourceColor(source: string): { bg: string; text: string } {
  const colors: Record<string, { bg: string; text: string }> = {
    ai_agent: { bg: '#ede9fe', text: '#6d28d9' },
    test: { bg: '#dbeafe', text: '#1d4ed8' },
    api: { bg: '#f1f5f9', text: '#475569' },
  };
  return colors[source] || { bg: '#f1f5f9', text: '#475569' };
}

function getSourceLabel(source: string): string {
  const labels: Record<string, string> = {
    ai_agent: 'AI Agent',
    test: 'Teste',
    api: 'API',
  };
  return labels[source] || source;
}
